<template>
  <component
    :is="tag"
    v-bind="bindings"
    :class="classes"
    @click="handleClick"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue"
import type { RouteLocationRaw } from "vue-router"

defineOptions({ inheritAttrs: false })

type CommonProps = {
  variant?: "default" | "info" | "danger"
}

type RouterLinkProps = {
  to: RouteLocationRaw
  href?: never
  target?: never
  disabled?: never
}

type AnchorProps = {
  href: string
  target?: string
  to?: never
  disabled?: never
}

type ButtonProps = {
  disabled?: boolean
  to?: never
  href?: never
  target?: never
}

type Props = CommonProps &
  (RouterLinkProps | AnchorProps | ButtonProps)

const props = defineProps<
  CommonProps &
    (RouterLinkProps | ButtonProps | AnchorProps)
>()

const emit = defineEmits<{
  click: [event: Event]
}>()

const attrs = useAttrs()

const fallthroughAttrs = computed(() => {
  const { class: _class, ...rest } = attrs
  return rest
})

const tag = computed(() => {
  if (props.to !== undefined) return "RouterLink"
  if (props.href !== undefined) return "a"
  return "button"
})

const bindings = computed(() => {
  if (props.to !== undefined) {
    return {
      to: props.to,
      ...fallthroughAttrs.value,
    }
  }

  if (props.href !== undefined) {
    return {
      href: props.href,
      target: props.target,
      rel: computedRel.value,
      ...fallthroughAttrs.value,
    }
  }

  return {
    type: "button",
    disabled: props.disabled,
    ...fallthroughAttrs.value,
  }
})

const computedRel = computed(() =>
  props.target === "_blank"
    ? "noopener noreferrer"
    : undefined
)

const variantClasses = {
  default: "text-base/80",
  info: "text-info/87",
  danger: "text-error/87",
} satisfies Record<Required<Props>["variant"], string>

const classes = computed(() => [
  "cursor-pointer hover:underline",
  variantClasses[props.variant ?? "default"],
  attrs.class,
])

const handleClick = (event: Event) => {
  if (props.disabled) {
    event.preventDefault()
    return
  }
  emit("click", event)
}
</script>
